/**
 * Canonical API-ingest system flow id is `system-api-ingest__${pipeline_id}`.
 * Data Flow ops used to navigate to `/dataflow/system-${pipeline_id}`
 * (`system-pipe_…`), which never matches a derived system flow.
 */

const PIPE_ID_RE = /pipe_[0-9a-fA-F-]{36}/;

export function ingestPipelineSystemFlowId(pipelineId: string): string {
  return `system-api-ingest__${String(pipelineId || '').trim()}`;
}

export function extractIngestPipelineId(routeId: string | null | undefined): string | null {
  const raw = String(routeId || '').trim();
  if (!raw) return null;
  const prefixed = raw.match(/^system-api-ingest__(.+)$/);
  if (prefixed?.[1]) return prefixed[1];
  const legacy = raw.match(/^system-(pipe_[0-9a-fA-F-]{36})$/);
  if (legacy?.[1]) return legacy[1];
  const loose = raw.match(PIPE_ID_RE);
  return loose ? loose[0] : null;
}

export function canonicalizeSystemFlowRouteId(routeId: string): string {
  const pipelineId = extractIngestPipelineId(routeId);
  if (!pipelineId) return routeId;
  if (routeId.startsWith('system-api-ingest__')) return routeId;
  return ingestPipelineSystemFlowId(pipelineId);
}

export function systemFlowListQuery(routeId: string): {
  category?: string;
  scope: string;
  limit: number;
} {
  if (extractIngestPipelineId(routeId)) {
    return { category: 'api_ingest', scope: 'all', limit: 200 };
  }
  return { scope: 'all', limit: 200 };
}

export function matchSystemFlow<T extends { id?: string; metadata?: { pipeline_id?: string | null } }>(
  flows: T[] | null | undefined,
  routeId: string
): T | null {
  const list = Array.isArray(flows) ? flows : [];
  const wanted = canonicalizeSystemFlowRouteId(routeId);
  const pipelineId = extractIngestPipelineId(routeId);
  return (
    list.find((flow) => flow.id === wanted || flow.id === routeId)
    || (pipelineId
      ? list.find((flow) => String(flow.metadata?.pipeline_id || '') === pipelineId)
      : undefined)
    || null
  );
}
